import { useState, useRef, useEffect, useMemo } from 'react'
import { Input } from 'react-daisyui'
import { IconPlus, IconX } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useClient } from '@/stores/useClient.ts'
import { ProfProject } from '@/models/profProject.ts'

interface ProjectSelectProps {
  selectedProjectIds: string[]
  onAdd: (projectId: string) => void
  onRemove: (projectId: string) => void
  hideBadges?: boolean
}

export default function ProjectSelect({ selectedProjectIds, onAdd, onRemove, hideBadges }: ProjectSelectProps) {
  const { getProjects } = useClient()
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const { data } = useQuery({
    queryKey: ['projects', { page: 1, page_size: 100 }],
    queryFn: async () => {
      const res = await getProjects({ page: 1, page_size: 100 })
      return res.data
    },
  })

  const allProjects = data?.results ?? []

  const selectedIds = new Set(selectedProjectIds)

  const selectedProjects = useMemo(() => {
    return allProjects.filter(p => selectedIds.has(p.id))
  }, [allProjects, selectedProjectIds])

  const filtered = allProjects
    .filter(p => !selectedIds.has(p.id))
    .filter(p => p.title.toLowerCase().includes(query.toLowerCase()))

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function handleSelect(project: ProfProject) {
    onAdd(project.id)
    setQuery('')
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className="relative">
      {!hideBadges && selectedProjects.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedProjects.map(project => (
            <span
              key={project.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-secondary"
            >
              {project.title}
              <button
                className="hover:opacity-70"
                onClick={() => onRemove(project.id)}
              >
                <IconX size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      <Input
        size="sm"
        className="w-full"
        placeholder="Projekt suchen..."
        value={query}
        onChange={e => { setQuery(e.target.value); setIsOpen(true) }}
        onFocus={() => setIsOpen(true)}
      />

      {isOpen && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-secondary border border-base-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtered.map(project => (
            <button
              key={project.id}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-secondary text-left text-sm"
              onClick={() => handleSelect(project)}
            >
              <IconPlus size={14} className="opacity-40" />
              {project.title}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
